<template>
  <canvas
    ref="canvas"
    class="marker-error-vectors"
    :style="boxStyle"
    aria-hidden="true"
  ></canvas>
</template>

<script>
/**
 * AVA-475 — the deviation arrows drawn on a completed point's as-built marker.
 *
 * Presentational only: props in, pixels out. Every decision about WHICH axes
 * draw, what colour they are and which way they point was already made by
 * `utils/errorVectors.js` and `VerificationViewer.projectMarkers()`. This
 * component knows nothing about tolerances, deltas or the viewer.
 *
 * WHY A `<canvas>` AND NOT SVG OR DIVS
 * The whole point of these arrows is that they reach the report. Evidence
 * capture rasterises the entire `.verification-markers` subtree with
 * html2canvas (utils/evidenceCapture.js:314-340), and of the element types
 * html2canvas supports, `<canvas>` is the one it composites by direct pixel
 * copy — no CSS re-implementation, no transform maths, no inline-SVG support
 * matrix. Inline SVG in this repo is unverified under html2canvas and rotated
 * div shafts would need transforms, which the marker CSS deliberately avoids
 * (VerificationViewer.vue:1479-1482).
 *
 * SIZED, NOT SCALED. Like the marker chips, every dimension is a base pixel
 * value multiplied by `scale` at draw time rather than a `transform: scale()`
 * on a fixed-size drawing. A transform would rasterise at base size and blur on
 * the way up — exactly what the marker SCSS comment warns about, and doubly bad
 * here because the capture path upscales again.
 */
import { ERROR_VECTOR_COLORS, formatMagnitudeMm } from '@/features/Verification/utils/errorVectors';

/**
 * Triad origin, in base px ABOVE the marker anchor.
 *
 * Derived, not picked, and the DOWNWARD arrow is what sets it — a Z deviation
 * below design is the common case, not an edge one. That arrow ends
 * `ARROW_LENGTH` below the origin and its number extends another
 * `LABEL_GAP + LABEL_FONT` past that: 34+5+12 = 51 px below the origin.
 *
 * It has to clear the as-built chip, whose top edge is further up than
 * `--vm-as-built` suggests: the chip is an 18 px border-box square ROTATED 45°
 * (VerificationViewer.vue `&--as-built &__shape`), so its bounding half-extent
 * is 9·√2 ≈ 12.7 px, plus the 1.5 px ring ≈ 14.2 px. 51 + 14.2 rounds up to 66
 * for contact; 72 leaves a deliberate ~7 px gap so the number never touches the
 * chip's top corner at any scale.
 */
const ORIGIN_OFFSET_PX = 72;

/**
 * Uniform for every arrow. Length is NOT a magnitude channel — colour and the
 * number carry that. See the "Ratified requirements" in the plan.
 */
const ARROW_LENGTH_PX = 34;
const HEAD_LENGTH_PX = 9;
const HEAD_HALF_WIDTH_PX = 5;
const SHAFT_WIDTH_PX = 2.5;
const DOT_RADIUS_PX = 4;

const LABEL_GAP_PX = 5;
const LABEL_FONT_PX = 12;
const LABEL_HALO_PX = 3;
/** Vertical pitch when several degenerate axes stack beside the origin dot. */
const LABEL_LINE_PX = 14;
/** Degenerate label sits beside the dot rather than at an arrow tip. */
const DOT_LABEL_DX_PX = 9;

/**
 * Half the drawing box, base px: the longest arrow plus its label plus a
 * margin. The canvas is centred on the triad origin, so this is the furthest
 * anything can be drawn from it before it would be clipped.
 */
const BOX_HALF_PX = ARROW_LENGTH_PX + LABEL_GAP_PX + 28;
const BOX_SIZE_PX = BOX_HALF_PX * 2;

/**
 * Backing-store density floor.
 *
 * Not `devicePixelRatio` alone. Evidence capture rasterises this overlay at a
 * scale derived from its 1600 px output target, INDEPENDENT of DPR
 * (evidenceCapture.js:327-336), so on a 1x display a DPR-sized bitmap would be
 * upscaled into the captured JPEG and the millimetre values would blur into
 * unreadable smudges on the report page. 2x costs 4x the pixels of a 128 px
 * box — nothing — and keeps the text legible after the capture upscale.
 */
const MIN_BACKING_DENSITY = 2;

/**
 * Canvas 2D cannot resolve CSS custom properties: assigning a `ctx.font` string
 * containing `var(--ds-font-mono, …)` fails to parse SILENTLY and leaves the
 * context at its default `10px sans-serif`. So the mono stack behind
 * `$vf-font-mono` (styles/_verification-tokens.scss:16) is repeated literally
 * here, minus the custom property, to keep these numbers in the same typeface
 * as the marker chips they belong to.
 */
const LABEL_FONT_STACK = "'IBM Plex Mono', 'Roboto Mono', monospace";

/** Dark outline behind every stroke — these are drawn over both the dark canvas and a bright point cloud. */
const HALO_COLOR = 'rgba(4, 14, 17, 0.9)';
/** Only reachable if a vector arrives with an unknown severity. Visible, not silent. */
const FALLBACK_COLOR = '#ffffff';

/** Halo pass first, coloured body second. */
const PAINT_PASSES = Object.freeze(['halo', 'body']);

/**
 * How far off-axis a direction may be before its label stops being placed to
 * that side and gets centred instead. Keeps a near-vertical arrow's number
 * above/below the tip rather than clipped to one side of it.
 */
const ALIGN_THRESHOLD = 0.35;

export default {
  name: 'MarkerErrorVectors',
  props: {
    /**
     * Complete, already-projected vectors:
     * `{ dx, dy, degenerate, valueMm, label, severity }`. `dx/dy` are unit
     * screen directions with the deviation's SIGN already applied by the
     * viewer, so this component never reasons about sign.
     */
    vectors: {
      type: Array,
      default: () => [],
    },
    /** The marker-size multiplier, same one behind `--marker-scale`. */
    scale: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    /**
     * Guards the one input that can silently blank the whole drawing: a NaN or
     * zero scale collapses every dimension, and the arrows would vanish with no
     * clue why. Same reasoning as the numeric fallbacks on `axesSize`.
     */
    safeScale() {
      return Number.isFinite(this.scale) && this.scale > 0 ? this.scale : 1;
    },
    /**
     * The canvas is a child of the ZERO-SIZE marker anchor, so it positions
     * itself entirely by explicit offsets — like the chip and the label do —
     * placing its centre `ORIGIN_OFFSET_PX` above the anchor.
     */
    boxStyle() {
      const size = BOX_SIZE_PX * this.safeScale;
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${-BOX_HALF_PX * this.safeScale}px`,
        top: `${(-ORIGIN_OFFSET_PX - BOX_HALF_PX) * this.safeScale}px`,
      };
    },
  },
  watch: {
    // Not deep: `projectMarkers` replaces the array wholesale on every camera
    // change (it has to — Vue 2 cannot observe properties added to an existing
    // entry), so identity always changes when the content does.
    vectors() {
      this.draw();
    },
    scale() {
      this.draw();
    },
  },
  mounted() {
    this.draw();
  },
  methods: {
    /**
     * `buildVectorModel` always supplies `label`; the fallback covers a vector
     * assembled by hand. It goes through the SAME formatter that produced the
     * label rather than `String(valueMm)`, which dropped the trailing zero and
     * wrote "5" where the model writes "5.0" — one number, two spellings.
     */
    labelFor(vector) {
      if (vector.label) return String(vector.label);
      return formatMagnitudeMm(vector.valueMm);
    },
    colorFor(vector) {
      return ERROR_VECTOR_COLORS[vector.severity] || FALLBACK_COLOR;
    },
    draw() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      const scale = this.safeScale;
      const cssSize = BOX_SIZE_PX * scale;
      const density = Math.max(window.devicePixelRatio || 1, MIN_BACKING_DENSITY);
      const backing = Math.max(1, Math.round(cssSize * density));

      // Assigning width/height also clears the canvas, so only touch them when
      // they actually changed and clear explicitly otherwise.
      if (canvas.width !== backing || canvas.height !== backing) {
        canvas.width = backing;
        canvas.height = backing;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Draw in CSS pixels; the transform absorbs the backing density. Derived
      // from the rounded backing rather than `density` so the rounding cannot
      // leave a half-pixel scale mismatch.
      const unit = backing / cssSize;
      ctx.setTransform(unit, 0, 0, unit, 0, 0);
      ctx.clearRect(0, 0, cssSize, cssSize);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const origin = cssSize / 2;
      const labels = [];
      let degenerateIndex = 0;

      this.vectors.forEach((vector) => {
        if (vector.degenerate) {
          this.paintDot(ctx, origin, origin, vector, scale);
          labels.push({
            vector,
            x: origin + DOT_LABEL_DX_PX * scale,
            y: origin + degenerateIndex * LABEL_LINE_PX * scale,
            align: 'left',
            baseline: 'middle',
          });
          degenerateIndex += 1;
          return;
        }
        this.paintArrow(ctx, origin, origin, vector, scale);
        labels.push(this.labelPlacement(vector, origin, origin, scale));
      });

      // Labels last, so no shaft is ever drawn across a number.
      labels.forEach((placement) => this.paintLabel(ctx, placement, scale));
    },

    /**
     * Shaft + filled head, painted twice: a wider dark pass for legibility
     * against a bright point cloud, then the severity colour on top.
     */
    paintArrow(ctx, ox, oy, vector, scale) {
      const length = ARROW_LENGTH_PX * scale;
      const head = HEAD_LENGTH_PX * scale;
      const halfWidth = HEAD_HALF_WIDTH_PX * scale;

      const tipX = ox + vector.dx * length;
      const tipY = oy + vector.dy * length;
      const baseX = ox + vector.dx * (length - head);
      const baseY = oy + vector.dy * (length - head);
      // Perpendicular to the direction, for the head's two back corners.
      const perpX = -vector.dy;
      const perpY = vector.dx;

      PAINT_PASSES.forEach((pass) => {
        const isHalo = pass === 'halo';
        const paint = isHalo ? HALO_COLOR : this.colorFor(vector);
        ctx.strokeStyle = paint;
        ctx.fillStyle = paint;
        ctx.lineWidth = (SHAFT_WIDTH_PX + (isHalo ? LABEL_HALO_PX : 0)) * scale;

        ctx.beginPath();
        ctx.moveTo(ox, oy);
        ctx.lineTo(baseX, baseY);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(baseX + perpX * halfWidth, baseY + perpY * halfWidth);
        ctx.lineTo(baseX - perpX * halfWidth, baseY - perpY * halfWidth);
        ctx.closePath();
        ctx.fill();
        // The halo pass strokes the head too, so its outline grows with it.
        if (isHalo) ctx.stroke();
      });
    },

    /**
     * An axis with no direction on screen — the depth axis of an orthographic
     * view. A dot says "measured, but this view cannot show which way", which
     * is the honest statement; an arrow in an arbitrary direction would not be.
     */
    paintDot(ctx, ox, oy, vector, scale) {
      PAINT_PASSES.forEach((pass) => {
        const isHalo = pass === 'halo';
        ctx.fillStyle = isHalo ? HALO_COLOR : this.colorFor(vector);
        ctx.beginPath();
        ctx.arc(ox, oy, (DOT_RADIUS_PX + (isHalo ? LABEL_HALO_PX / 2 : 0)) * scale, 0, Math.PI * 2);
        ctx.fill();
      });
    },

    /** Where an arrow's number sits: just past the tip, on the far side of it. */
    labelPlacement(vector, ox, oy, scale) {
      const reach = (ARROW_LENGTH_PX + LABEL_GAP_PX) * scale;
      let align = 'center';
      if (vector.dx > ALIGN_THRESHOLD) align = 'left';
      else if (vector.dx < -ALIGN_THRESHOLD) align = 'right';

      let baseline = 'middle';
      if (vector.dy > ALIGN_THRESHOLD) baseline = 'top';
      else if (vector.dy < -ALIGN_THRESHOLD) baseline = 'bottom';

      return {
        vector,
        x: ox + vector.dx * reach,
        y: oy + vector.dy * reach,
        align,
        baseline,
      };
    },

    paintLabel(ctx, placement, scale) {
      const text = this.labelFor(placement.vector);
      if (!text) return;

      ctx.font = `600 ${LABEL_FONT_PX * scale}px ${LABEL_FONT_STACK}`;
      ctx.textAlign = placement.align;
      ctx.textBaseline = placement.baseline;

      // strokeText UNDER fillText: the halo is drawn centred on the glyph
      // outline, so half of it would cover the glyph if painted after.
      ctx.lineWidth = LABEL_HALO_PX * scale;
      ctx.strokeStyle = HALO_COLOR;
      ctx.miterLimit = 2;
      ctx.strokeText(text, placement.x, placement.y);

      ctx.fillStyle = this.colorFor(placement.vector);
      ctx.fillText(text, placement.x, placement.y);
    },
  },
};
</script>

<style lang="scss" scoped>
.marker-error-vectors {
  position: absolute;
  display: block;
  /* The marker layer is already click-through, but this canvas is the largest
     box in it — keep it from becoming the exception. */
  pointer-events: none;
}
</style>
